import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deleteTeam, fetchTeamsData, fetchTeamUsers } from "../../Redux/Thunks/teamsThunk";
import { RiDeleteBinFill, RiPencilLine, RiUserLine, RiUserAddLine } from "react-icons/ri";
import { ContentLoader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";
import { DEFAULT_LIST_PAGE_SIZE } from "../../Utility/Pagination/listPaginationConstants";
import TeamFilter from "./TeamFilter";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import Avatar from "../../Utility/Avatar/Avatar";

import "../../Pages/Users/users.css";
import { getListFromApiResponse, mapTeamToTableRow } from "./teamMappers";
import ViewTeamMembersModal from "./TeamMembers/ViewTeamMembersModal";
import AddTeamMemberModal from "./TeamMembers/AddTeamMemberModal";
import EditTeamMemberModal from "./TeamMembers/EditTeamMemberModal";
import RemoveTeamMemberModal from "./TeamMembers/RemoveTeamMemberModal";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";

const TeamList = ({
  teamsData,
  loading,
  searchTerm,
  currentPage,
  setCurrentPage,
  limit = DEFAULT_LIST_PAGE_SIZE,
  onPageSizeChange,
  OpenModal,
  listingFilter,
  handleFilterChange,
  handleFilterReset,
  applyFilters,
  onEditTeam,
  onRefetchTeams,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [viewMembersOpen, setViewMembersOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [removeMember, setRemoveMember] = useState(null);
  const [membersByTeamId, setMembersByTeamId] = useState({});
  const [viewMembersLoading, setViewMembersLoading] = useState(false);
  const [removeSuccessMsg, setRemoveSuccessMsg] = useState("");
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);

  const menuRefs = useRef({});
  const { can } = usePermissions();

  const canEditTeam = can(PERMISSION_KEYS.TEAMS_EDIT);
  const canViewTeamMembers = can(PERMISSION_KEYS.TEAMS_VIEW);
  const canAddTeamMember = can(PERMISSION_KEYS.TEAMS_ADD_MEMBER);
  const canDeleteTeamPerm = can(PERMISSION_KEYS.TEAMS_DELETE);
  const showActionsColumn =
    canEditTeam || canViewTeamMembers || canAddTeamMember || canDeleteTeamPerm;
  const colCount = showActionsColumn ? 3 : 2;

  const teamMembers = selectedTeam ? membersByTeamId[selectedTeam.id] || [] : [];

  // Normalize team user from API: role object -> string, use API status (e.g. ACTIVE/INACTIVE) from user or team
  const normalizeTeamUser = (user) => {
    const apiStatus = user?.status ?? user?.team?.status;
    const statusDisplay =
      apiStatus === "ACTIVE" || apiStatus === "Active"
        ? "Active"
        : apiStatus === "INACTIVE" || apiStatus === "Inactive"
          ? "Inactive"
          : apiStatus === "Invited"
            ? "Invited"
            : (apiStatus ?? (user?.email_verified === 1 ? "Active" : "Invited"));
    const roleName =
      typeof user?.role === "object" && user?.role != null ? user.role?.name : user?.role;
    return {
      ...user,
      roleId: user?.role?.id ?? user?.role_id ?? user?.roleId ?? null,
      role: roleName ?? (typeof user?.role === "string" ? user.role : null) ?? "—",
      status: statusDisplay,
    };
  };

  // Fetch team users when View Members modal opens (GET /teams/:teamId/users)
  useEffect(() => {
    if (!viewMembersOpen || !selectedTeam?.id) {
      setViewMembersLoading(false);
      return;
    }
    let cancelled = false;
    setViewMembersLoading(true);
    dispatch(fetchTeamUsers(selectedTeam.id))
      .unwrap()
      .then((payload) => {
        if (cancelled) return;
        const list = getListFromApiResponse(payload).map(normalizeTeamUser);
        setMembersByTeamId((prev) => ({
          ...prev,
          [selectedTeam.id]: list,
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setMembersByTeamId((prev) => ({
            ...prev,
            [selectedTeam.id]: [],
          }));
        }
      })
      .finally(() => {
        if (!cancelled) setViewMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMembersOpen, selectedTeam?.id, dispatch]);

  const openViewMembers = (team) => {
    setSelectedTeam(team);
    setViewMembersOpen(true);
    setOpenMenuId(null);
  };

  const openAddMember = () => {
    if (!canAddTeamMember) {
      setDeniedPermissionKey(PERMISSION_KEYS.TEAMS_ADD_MEMBER);
      return;
    }
    setAddMemberOpen(true);
  };

  const closeViewMembers = () => {
    setViewMembersOpen(false);
    setSelectedTeam(null);
  };

  const handleAddMemberSuccess = (data) => {
    const teamId = data.teamId ?? selectedTeam?.id;
    if (!teamId) return;
    // Refetch team users so View Members shows the latest from API
    dispatch(fetchTeamUsers(teamId))
      .unwrap()
      .then((payload) => {
        const list = getListFromApiResponse(payload).map(normalizeTeamUser);
        setMembersByTeamId((prev) => ({ ...prev, [teamId]: list }));
      })
      .catch(() => {});
  };

  const handleEditMemberSuccess = (updated) => {
    const teamId = selectedTeam?.id;
    if (!teamId) return;
    setMembersByTeamId((prev) => {
      const list = prev[teamId] || [];
      return {
        ...prev,
        [teamId]: list.map((m) =>
          m.id === updated.id
            ? {
                ...m,
                role: updated.role,
                roleId: updated.roleId ?? m.roleId,
              }
            : m
        ),
      };
    });
    setEditMember(null);
  };

  const handleRemoveMemberConfirm = (member) => {
    const teamId = selectedTeam?.id;
    if (!teamId) return;
    setMembersByTeamId((prev) => ({
      ...prev,
      [teamId]: (prev[teamId] || []).filter((m) => m.id !== member.id),
    }));
    setRemoveMember(null);
    setRemoveSuccessMsg("Member removed successfully");
    setTimeout(() => setRemoveSuccessMsg(""), 3000);
  };

  // List from API (already filtered by name & status on server)
  const listFromApi = teamsData?.list ?? [];
  const meta = teamsData?.meta ?? null;

  // Optional client-side sort on current page
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(listFromApi, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  // Pagination from API meta (list is already one page)
  const totalCount = meta?.totalCount ?? meta?.total ?? listFromApi.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / (limit || DEFAULT_LIST_PAGE_SIZE)));
  const paginatedTeams = sortedData;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(menuRefs.current).forEach((id) => {
        if (menuRefs.current[id] && !menuRefs.current[id].contains(event.target)) {
          setOpenMenuId(null);
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = (teamId) => {
    setOpenMenuId(openMenuId === teamId ? null : teamId);
  };

  const handleDeleteClick = (team) => {
    if (!canDeleteTeamPerm) {
      setDeniedPermissionKey(PERMISSION_KEYS.TEAMS_DELETE);
      return;
    }
    setOpenMenuId(null);
    setTeamToDelete(team);
  };

  const handleConfirmDelete = async () => {
    if (!teamToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await dispatch(deleteTeam(teamToDelete.id)).unwrap();
      if (onRefetchTeams) onRefetchTeams();
      else dispatch(fetchTeamsData({ page: 1, limit: limit || DEFAULT_LIST_PAGE_SIZE }));
      setTeamToDelete(null);
    } catch (err) {
      // Keep modal open on error
    } finally {
      setDeleteLoading(false);
    }
  };

  // Get avatar background color based on team name using theme colors
  const getTeamAvatarColor = (teamName) => {
    if (!teamName) return "var(--badge-success)"; // Default to success color

    // Use theme CSS variables for consistent colors
    const themeColors = [
      "var(--badge-success)", // #127c5a (green)
      "var(--badge-info)", // #0a91d5 (blue)
      "var(--badge-warning)", // #d7a741 (yellow/gold)
      "var(--badge-danger)", // #8b1d36 (red/maroon)
      "var(--card-variant-2)", // #09b570 (green)
      "var(--card-variant-3)", // #e7683a (orange)
      "var(--badge-secondary)", // #6c757d (gray)
      "var(--card-variant-1)", // #04a4a8 (teal)
    ];

    const index = teamName.charCodeAt(0) % themeColors.length;
    return themeColors[index];
  };

  const hasData = (teamsData?.list ?? []).length > 0;

  return (
    <div className="clients-page-content !bg-transparent">
      <div className="w-full ">
        <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
          {/* Filter Section */}
          <div className="mb-3">
            <TeamFilter
              listingFilter={listingFilter}
              handleFilterChange={handleFilterChange}
              handleFilterReset={handleFilterReset}
              applyFilters={applyFilters}
            />
          </div>

          {/* Table Card */}
          <div>
            <div className="w-full">
              <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md  shadow-sm transition-shadow">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse mb-0 clients-table ">
                        <thead className="bg-surface-secondary">
                          <tr>
                            <SortableHeader
                              columnKey="teamName"
                              label="Team Name"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="status"
                              label="Status"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            {showActionsColumn ? (
                              <SortableHeader
                                columnKey="action"
                                label="Action"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="action-col"
                              />
                            ) : null}
                          </tr>
                        </thead>
                        <tbody className="relative">
                          {loading ? (
                            <tr aria-hidden="true">
                              <td colSpan={colCount} className="min-h-[240px] h-60 p-0 border-0" />
                            </tr>
                          ) : (
                            <>
                              {paginatedTeams.length > 0 ? (
                                paginatedTeams.map((team) => {
                                  const row = mapTeamToTableRow(team);
                                  return (
                                    <tr key={team.id}>
                                      <td className="text-start align-middle">
                                        <div className="flex items-center gap-2">
                                          <Avatar
                                            name={row.teamName}
                                            colorClass=""
                                            style={{
                                              backgroundColor: getTeamAvatarColor(row.teamName),
                                            }}
                                          />
                                          <Typography variant="span1">{row.teamName}</Typography>
                                        </div>
                                      </td>

                                      <td className="text-start align-middle">
                                        <Typography
                                          variant="span1"
                                          className={`badge rounded-full px-3 py-1 text-white text-xs font-bold ${row.statusColorClass}`}
                                          component="span"
                                        >
                                          {row.status}
                                        </Typography>
                                      </td>

                                      {showActionsColumn ? (
                                        <td className="text-start align-middle content-cell">
                                          <div className="flex gap-0.5 items-center justify-end gap-2">
                                            {canEditTeam ? (
                                              <FloatingTooltip label="Edit Team">
                                                <Button
                                                  onClick={() => onEditTeam?.(team)}
                                                  aria-label="Edit Team"
                                                  size="sm"
                                                  className="
                                        btn-warning-new
                                        !w-7 !h-7  
                                        !p-0 
                                        !font-normal 
                                        !leading-none
                                        flex items-center justify-center
                                        border-0
                                      "
                                                >
                                                  <RiPencilLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canViewTeamMembers ? (
                                              <FloatingTooltip label="View Members">
                                                <Button
                                                  type="button"
                                                  onClick={() => openViewMembers(team)}
                                                  aria-label="View Members"
                                                  size="sm"
                                                  className="btn-info-new !w-7 !h-7  !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiUserLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canAddTeamMember ? (
                                              <FloatingTooltip label="Add Member">
                                                <Button
                                                  type="button"
                                                  onClick={() => {
                                                    setSelectedTeam(team);
                                                    setAddMemberOpen(true);
                                                  }}
                                                  aria-label="Add Member"
                                                  size="sm"
                                                  className="btn-success-new !w-7 !h-7  !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiUserAddLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canDeleteTeamPerm ? (
                                              <FloatingTooltip label="Delete Team">
                                                <Button
                                                  onClick={() => handleDeleteClick(team)}
                                                  aria-label="Delete Team"
                                                  size="sm"
                                                  className="btn-danger-new !w-7 !h-7  !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiDeleteBinFill className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}
                                          </div>
                                        </td>
                                      ) : null}
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={colCount} className="text-center">
                                    <NoDataFound
                                      message={
                                        searchTerm
                                          ? "No teams found matching your search"
                                          : "No teams available"
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center mt-2 text-center sm:text-start"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(teamsData?.list ?? []).length > 0 && (
            <ListPaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={limit}
              onPageChange={handlePageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </ContentLoader>
      </div>
      {/* Success toast for remove member */}
      {removeSuccessMsg && (
        <div className="mb-3 p-3 rounded-md bg-badge-success/15 text-badge-success text-sm font-medium">
          {removeSuccessMsg}
        </div>
      )}

      <ViewTeamMembersModal
        isOpen={viewMembersOpen}
        onClose={closeViewMembers}
        team={selectedTeam}
        members={teamMembers}
        membersLoading={viewMembersLoading}
        onAddMember={openAddMember}
        onEditMember={(member) => {
          if (!can(PERMISSION_KEYS.TEAMS_EDIT)) {
            setDeniedPermissionKey(PERMISSION_KEYS.TEAMS_EDIT);
            return;
          }
          setEditMember(member);
        }}
        onResendInvitation={(member) => {
          // TODO: API resend invitation
        }}
        onRemoveMember={(member) => {
          if (!can(PERMISSION_KEYS.TEAMS_EDIT)) {
            setDeniedPermissionKey(PERMISSION_KEYS.TEAMS_EDIT);
            return;
          }
          setRemoveMember(member);
        }}
      />

      <AddTeamMemberModal
        isOpen={addMemberOpen}
        onClose={() => {
          setAddMemberOpen(false);
          if (!viewMembersOpen) setSelectedTeam(null);
        }}
        team={selectedTeam}
        excludeMemberEmails={teamMembers.map((m) => m.email).filter(Boolean)}
        onSuccess={handleAddMemberSuccess}
      />

      <EditTeamMemberModal
        isOpen={!!editMember}
        onClose={() => setEditMember(null)}
        member={editMember}
        teamId={selectedTeam?.id}
        onSuccess={handleEditMemberSuccess}
      />

      <RemoveTeamMemberModal
        isOpen={!!removeMember}
        onClose={() => setRemoveMember(null)}
        member={removeMember}
        teamId={selectedTeam?.id}
        onSuccess={handleRemoveMemberConfirm}
      />

      <DeleteConfirmModal
        isOpen={!!teamToDelete}
        onClose={() => setTeamToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete team"
        itemName={teamToDelete?.teamName ?? teamToDelete?.name}
        loading={deleteLoading}
      />
      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
};

export default TeamList;
